"use client";

import { useEffect } from "react";
import { useVisitorProfilePreferencesQuery } from "@/lib/graphql/queries/generated/profile.generated";
import { useDemoPrefs } from "./runtime-demo-prefs";
import { setRuntimeLocale } from "./runtime-locale";
import type { ThemePreference } from "./use-theme-mode";

/** Subset of `VisitorProfile` the shell needs to apply theme + language +
 *  notification preferences. `loaded` is `true` once the BE has answered
 *  (or never started, in the demo/unauthed case) so consumers don't apply
 *  stale defaults while the query is in flight. */
export interface VisitorProfilePreferences {
  themeMode: ThemePreference | null;
  themeModeEditable: boolean;
  language: string | null;
  desktopNotifications: boolean;
  loaded: boolean;
}

const EMPTY: VisitorProfilePreferences = {
  themeMode: null,
  themeModeEditable: true,
  language: null,
  desktopNotifications: false,
  loaded: false,
};

/**
 * Fetches the visitor's profile (when Profile is enabled + the visitor is
 * authed) and surfaces just the preferences slice — used by the shell to
 * apply theme mode, language, and desktop-notification opt-in across the
 * whole widget. Skips entirely when the feature is off or the visitor is
 * anonymous (the BE rejects the query without a visitor JWT).
 *
 * Persists the resolved language to BOTH:
 *
 *   1. the `wexio-locale` cookie — so the NEXT SSR pass / middleware
 *      hands back the correct locale without waiting for the query, and
 *   2. the runtime-locale signal — so the active `RuntimeIntlProvider`
 *      swaps its messages bundle live in this session (no reload).
 */
export function useVisitorProfilePreferences(opts: {
  enabled: boolean;
  /** Demo mode — read from the in-memory `runtime-demo-prefs` bus instead
   *  of the BE-backed Apollo query. Lets the visitor's pref changes in
   *  the demo ProfileTab flow through to the rest of the widget (theme
   *  flip, language switch, notification toggle) without a network
   *  round-trip. */
  isDummy?: boolean;
}): VisitorProfilePreferences {
  // Slim prefs-only query — drops the heavy `fields[]` payload that
  // ProfileTab needs. Same resolver / same parameterless cache slot,
  // so ProfileTab's full read supersedes this one once that tab
  // opens, and the save mutation flips the cache for both consumers.
  // SKIPPED in demo mode (no BE).
  const { data } = useVisitorProfilePreferencesQuery({
    skip: !opts.enabled || !!opts.isDummy,
  });
  const profile = data?.visitorProfile;
  const demo = useDemoPrefs();

  const prefs: VisitorProfilePreferences = opts.isDummy
    ? {
        themeMode: demo.themeMode,
        themeModeEditable: true,
        language: demo.language,
        desktopNotifications: demo.desktopNotifications,
        loaded: true,
      }
    : profile
      ? {
          themeMode: normaliseTheme(profile.preferences.themeMode),
          themeModeEditable: profile.themeModeEditable,
          language: profile.preferences.language ?? null,
          desktopNotifications: !!profile.preferences.desktopNotifications,
          loaded: true,
        }
      : opts.enabled
        ? EMPTY
        : { ...EMPTY, loaded: true }; // disabled → "loaded" so consumers don't wait forever

  // Persist the language pref to the cookie (next SSR pass) AND push it
  // to the runtime-locale bus so `RuntimeIntlProvider` swaps the active
  // messages bundle WITHOUT a reload. Idempotent: `setRuntimeLocale` is a
  // no-op when the value matches the current one or isn't in the
  // supported set.
  useEffect(() => {
    if (!prefs.loaded) return;
    if (!prefs.language) return;
    try {
      // biome-ignore lint/suspicious/noDocumentCookie: synchronous one-shot write the next SSR pass reads — Cookie Store API is async and still partial in Safari, so plain document.cookie is the right tool here.
      document.cookie = `wexio-locale=${encodeURIComponent(prefs.language)}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    } catch {}
    setRuntimeLocale(prefs.language);
  }, [prefs.loaded, prefs.language]);

  return prefs;
}

/** Normalise the BE enum to the lowercase set our theme hook + CSS
 *  expects. BE validates against `system|light|dark`; legacy values
 *  (`AUTO`/uppercase) are accepted for forward-compat. */
function normaliseTheme(value: unknown): ThemePreference | null {
  if (typeof value !== "string") return null;
  const v = value.toLowerCase();
  if (v === "light" || v === "dark") return v;
  if (v === "auto" || v === "system") return "auto";
  return null;
}
